// Sprint T-22 · Notification Center · declarative kinds → UI map
//
// SOURCE OF TRUTH: mirrors NOTIFICATION_KIND_ROUTES from
// /app/backend/notifications/routing_table.py · los 7 kinds oficiales
// AG-Ecosystem (In-App/Chat + Push + Email). Sin SMS/WhatsApp jamás.
//
// Este módulo es puramente declarativo · ningún side effect.
// Se importa desde NotificationCenter, NotificationItem, NotificationFilters
// y NotificationPreferencesPanel para render consistente de labels/iconos.

import {
  ChatCircle,
  CalendarCheck,
  FileText,
  ShieldWarning,
  Star,
  Megaphone,
  Warning,
  Bell,
  CheckCircle,
} from "@phosphor-icons/react";

// Los 7 kinds oficiales (T-21 F1d) + 1 aditivo T-24 (appointment_confirmed) · en el mismo
// orden en el que se muestran en filtros y en el panel de preferencias.
// NO añadir nuevos sin GO del Founder.
export const OFFICIAL_KINDS = [
  "chat_message",
  "appointment_reminder",
  "appointment_confirmed",
  "document_ready",
  "security_alert",
  "review_request",
  "system_announcement",
  "automation_failed",
];

// Diccionario declarativo · UI-only.
//   · label     → texto que ve el usuario en filtros y en la matriz de prefs.
//   · category  → agrupador para el panel de preferencias.
//   · icon      → Phosphor icon component (usado por NotificationItem).
//   · channels  → qué canales están soportados para ese kind (matriz de prefs).
//                 Si un canal NO está en esta lista, se muestra como “—” (no editable).
//   · locked    → true si el usuario NO puede modificar sus preferencias
//                 (security_alert · §7 directriz Founder).
export const KINDS_MAP = {
  chat_message: {
    label: "Mensajes",
    category: "Mensajes",
    icon: ChatCircle,
    channels: ["in_app", "push"],
    locked: false,
    description:
      "Mensajes de chat entre tu taller y tus clientes (o del equipo interno).",
  },
  appointment_reminder: {
    label: "Recordatorios de cita",
    category: "Citas",
    icon: CalendarCheck,
    channels: ["push", "email"],
    locked: false,
    description:
      "Avisos de próximas citas con fallback automático a email si el push falla.",
  },
  appointment_confirmed: {
    label: "Confirmación de cita",
    category: "Citas",
    icon: CheckCircle,
    channels: ["in_app", "push"],
    locked: false,
    description:
      "Aviso inmediato cuando una reserva se confirma o el taller acepta una cita pendiente de validación.",
  },
  document_ready: {
    label: "Documentos listos",
    category: "Documentos",
    icon: FileText,
    channels: ["in_app", "email"],
    locked: false,
    description:
      "Facturas, informes AEAT y otros documentos generados por el sistema.",
  },
  security_alert: {
    label: "Alertas de seguridad",
    category: "Seguridad",
    icon: ShieldWarning,
    channels: ["in_app", "push", "email"],
    locked: true, // NO se puede desactivar (backend rechaza 400)
    description:
      "Avisos críticos de seguridad de tu cuenta. Por tu protección, no se pueden desactivar.",
  },
  review_request: {
    label: "Peticiones de valoración",
    category: "Valoraciones",
    icon: Star,
    channels: ["push", "email"],
    locked: false,
    description:
      "Invitaciones para que tus clientes valoren el servicio recibido tras una cita.",
  },
  system_announcement: {
    label: "Novedades del sistema",
    category: "Sistema",
    icon: Megaphone,
    channels: ["in_app", "email"],
    locked: false,
    description:
      "Anuncios del equipo AutoGlow: nuevas funcionalidades, cambios importantes.",
  },
  automation_failed: {
    label: "Automatizaciones fallidas",
    category: "Sistema",
    icon: Warning,
    channels: ["in_app", "email"],
    locked: false,
    description:
      "Notificaciones cuando un job automático de tu taller ha fallado.",
  },
};

// Canales oficiales AG-Ecosystem · orden estable para render de columnas.
export const OFFICIAL_CHANNELS = [
  { key: "in_app", label: "In-App" },
  { key: "push", label: "Push" },
  { key: "email", label: "Email" },
];

// Devuelve el metadato de un kind desconocido (nunca debería ocurrir en runtime,
// pero cubre el edge case por si el backend emite un kind legacy).
export function getKindMeta(kind) {
  return (
    KINDS_MAP[kind] || {
      label: kind || "Notificación",
      category: "Otro",
      icon: Bell,
      channels: ["in_app"],
      locked: false,
      description: "",
    }
  );
}

// Chips de filtro por estado (leído / no leído / todas).
export const READ_FILTERS = [
  { key: "all", label: "Todas" },
  { key: "unread", label: "No leídas" },
  { key: "read", label: "Leídas" },
];

export default KINDS_MAP;
